import { useState, useEffect, useMemo } from 'react';
import { getLogoPath } from '../../utils/brandLogoMapper';

interface BrandLogoProps {
  brandName: string;
  className?: string;
}

let logoPathCache = new Map<string, string>();

// .ico files render with black backgrounds in <img> tags
const isProblematicFormat = (path: string) => /\.ico$/i.test(path);

export function BrandLogo({ brandName, className }: BrandLogoProps) {
  const [logoPath, setLogoPath] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);

  const memoizedBrandName = useMemo(() => brandName, [brandName]);

  useEffect(() => {
    if (logoPathCache.has(memoizedBrandName)) {
      setLogoPath(logoPathCache.get(memoizedBrandName) || null);
      return;
    }

    const fetchLogo = async () => {
      const path = await getLogoPath(memoizedBrandName);
      logoPathCache.set(memoizedBrandName, path);
      setLogoPath(path);
    };
    fetchLogo();
  }, [memoizedBrandName]);

  useEffect(() => {
    setImgError(false);
  }, [logoPath]);

  if (!logoPath || imgError || isProblematicFormat(logoPath)) {
    return (
      <div
        className={className}
        style={{
          height: '50px',
          width: '50px',
          backgroundColor: '#f0f0f0',
          borderRadius: '4px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          color: '#999'
        }}
      >
        {brandName.charAt(0).toUpperCase()}
      </div>
    );
  }

  return (
    <img
      src={logoPath}
      alt={brandName}
      className={className}
      onError={() => setImgError(true)}
      style={{
        height: '50px',
        width: '50px',
        objectFit: 'contain',
        backgroundColor: '#ffffff',
        borderRadius: '4px',
        padding: '2px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)'
      }}
    />
  );
}

export default BrandLogo;
